import { useState, FormEvent, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";

const Register = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { user, loading: authLoading, register, loginWithGoogle, loginWithGithub } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    // Client-side validation
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (name.trim().length < 2) {
      setError("Name must be at least 2 characters.");
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name.trim());
      // Ensure the local loading indicator is reset promptly after a successful registration
      setLoading(false);
      navigate("/");
    } catch (err: any) {
      // Human-readable error messages
      let errorMessage = "Failed to create account. Please try again.";
      
      if (err.code === "auth/email-already-in-use") {
        errorMessage = "An account with this email already exists.";
      } else if (err.code === "auth/invalid-email") {
        errorMessage = "Invalid email address.";
      } else if (err.code === "auth/weak-password") {
        errorMessage = "Password is too weak. Please use a stronger password.";
      } else if (err.code === "auth/operation-not-allowed") {
        errorMessage = "Email/password accounts are not enabled.";
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Redirect away from register page when user is authenticated
  useEffect(() => {
    // Wait until auth state has resolved
    if (authLoading) return;

    if (user) {
      // Clear any local UI state that might remain set during navigation
      setError("");
      setLoading(false);
      navigate("/", { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handleGoogleLogin = async () => {
    setError("");
    setLoading(true);
    try {
      await loginWithGoogle();
      // Reset local loading indicator and navigate
      setLoading(false);
      navigate("/");
    } catch (err: any) {
      // Log complete error details
      console.error("=== GOOGLE LOGIN ERROR DEBUG ===");
      console.error("Error code:", err.code);
      console.error("Error message:", err.message);
      console.error("Error full object:", err);
      if (err.customData) {
        console.error("Custom data:", err.customData);
      }
      if (err.credential) {
        console.error("Credential:", err.credential);
      }
      console.error("=================================");
      
      // Show exact error code + human-readable message
      const errorCode = err.code || "unknown";
      let humanMessage = "Failed to sign in with Google.";
      if (errorCode === "auth/popup-closed-by-user") {
        humanMessage = "Sign-in popup was closed.";
      } else if (errorCode === "auth/cancelled-popup-request") {
        humanMessage = "Sign-in was cancelled.";
      } else if (errorCode === "auth/operation-not-allowed") {
        humanMessage = "Google sign-in is not enabled. Please contact support.";
      } else if (errorCode === "auth/unauthorized-domain") {
        humanMessage = "This domain is not authorized for Google sign-in.";
      } else if (errorCode === "auth/account-exists-with-different-credential") {
        humanMessage = "An account already exists with this email using a different sign-in method.";
      }
      setError(`${humanMessage} [Code: ${errorCode}]`);
    } finally {
      setLoading(false);
    }
  };

  const handleGithubLogin = async () => {
    setError("");
    setLoading(true);
    try {
      await loginWithGithub();
      // Reset local loading indicator and navigate
      setLoading(false);
      navigate("/");
    } catch (err: any) {
      // Log complete error details
      console.error("=== GITHUB LOGIN ERROR DEBUG ===");
      console.error("Error code:", err.code);
      console.error("Error message:", err.message);
      console.error("Error full object:", err);
      if (err.customData) {
        console.error("Custom data:", err.customData);
      }
      if (err.credential) {
        console.error("Credential:", err.credential);
      }
      console.error("=================================");
      
      // Show exact error code + human-readable message
      const errorCode = err.code || "unknown";
      let humanMessage = "Failed to sign in with GitHub.";
      if (errorCode === "auth/popup-closed-by-user") {
        humanMessage = "Sign-in popup was closed.";
      } else if (errorCode === "auth/cancelled-popup-request") {
        humanMessage = "Sign-in was cancelled.";
      } else if (errorCode === "auth/operation-not-allowed") {
        humanMessage = "GitHub sign-in is not enabled. Please contact support.";
      } else if (errorCode === "auth/unauthorized-domain") {
        humanMessage = "This domain is not authorized for GitHub sign-in.";
      } else if (errorCode === "auth/account-exists-with-different-credential") {
        humanMessage = "An account already exists with this email using a different sign-in method.";
      }
      setError(`${humanMessage} [Code: ${errorCode}]`);
    } finally {
      setLoading(false);
    }
  };

  // Don't render the registration form if the auth state is still resolving or the user is already signed in
  if (authLoading) return null;
  if (user) return null;

  return (
    <section className="mx-auto max-w-md px-4 py-24 text-white">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-xl shadow-[0_0_40px_rgba(34,211,238,0.15)]"
      >
        <h1 className="font-orbitron text-2xl uppercase tracking-[0.4em] text-hacki-cyan mb-2">
          Register
        </h1>
        <p className="text-sm text-white/70 mb-8">
          Create your Hackiware account
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <input
              type="text"
              placeholder="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={loading}
              className="w-full rounded-full border border-white/10 bg-black/40 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-cyan-400/50 focus:outline-none focus:ring-1 focus:ring-cyan-400/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          <div>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              className="w-full rounded-full border border-white/10 bg-black/40 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-cyan-400/50 focus:outline-none focus:ring-1 focus:ring-cyan-400/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password (min. 6 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              className="w-full rounded-full border border-white/10 bg-black/40 px-4 py-3 pr-12 text-sm text-white placeholder:text-white/40 focus:border-cyan-400/50 focus:outline-none focus:ring-1 focus:ring-cyan-400/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            />

            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center text-white"
            >
              {showPassword ? (
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-9-7s4-7 9-7c1.19 0 2.332.21 3.375.6M3 3l18 18" />
                </svg>
              ) : (
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" d="M2.5 12s4-7 9.5-7 9.5 7 9.5 7-4 7-9.5 7S2.5 12 2.5 12z" />
                  <circle cx="12" cy="12" r="2.5" strokeWidth="1.5" />
                </svg>
              )}
            </button>
          </div>

          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
              className="w-full rounded-full border border-white/10 bg-black/40 px-4 py-3 pr-12 text-sm text-white placeholder:text-white/40 focus:border-cyan-400/50 focus:outline-none focus:ring-1 focus:ring-cyan-400/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            />

            <button
              type="button"
              onClick={() => setShowConfirmPassword((s) => !s)}
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center text-white"
            >
              {showConfirmPassword ? (
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-9-7s4-7 9-7c1.19 0 2.332.21 3.375.6M3 3l18 18" />
                </svg>
              ) : (
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" d="M2.5 12s4-7 9.5-7 9.5 7 9.5 7-4 7-9.5 7S2.5 12 2.5 12z" />
                  <circle cx="12" cy="12" r="2.5" strokeWidth="1.5" />
                </svg>
              )}
            </button>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300"
            >
              {error}
            </motion.div>
          )}

          <motion.button
            type="submit"
            disabled={loading}
            whileHover={{ scale: loading ? 1 : 1.02 }}
            whileTap={{ scale: loading ? 1 : 0.98 }}
            className="mt-2 rounded-full border border-white/20 bg-white/10 px-6 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow-[0_8px_30px_rgba(0,0,0,0.35)] backdrop-blur-lg transition duration-300 hover:border-cyan-400/50 hover:bg-cyan-500/10 hover:text-cyan-200 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              boxShadow: "0 0 18px rgba(34,211,238,0.2)",
            }}
          >
            {loading ? "Creating account..." : "Register"}
          </motion.button>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/10"></div>
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white/5 px-2 text-white/50">Or</span>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <motion.button
            type="button"
            onClick={handleGoogleLogin}
            disabled={loading}
            whileHover={{ scale: loading ? 1 : 1.01 }}
            whileTap={{ scale: loading ? 1 : 0.99 }}
            className="flex items-center justify-center gap-3 rounded-full border border-white/20 bg-white/5 px-4 py-2.5 text-sm font-medium text-white/90 backdrop-blur-sm transition hover:bg-white/10 hover:border-white/30 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </motion.button>

          <motion.button
            type="button"
            onClick={handleGithubLogin}
            disabled={loading}
            whileHover={{ scale: loading ? 1 : 1.01 }}
            whileTap={{ scale: loading ? 1 : 0.99 }}
            className="flex items-center justify-center gap-3 rounded-full border border-white/20 bg-white/5 px-4 py-2.5 text-sm font-medium text-white/90 backdrop-blur-sm transition hover:bg-white/10 hover:border-white/30 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
            </svg>
            Continue with GitHub
          </motion.button>
        </div>

        <div className="mt-6 text-center text-sm text-white/60">
          Already have an account?{" "}
          <Link
            to="/login"
            className="text-cyan-400 hover:text-cyan-300 transition-colors underline underline-offset-2"
          >
            Login
          </Link>
        </div>
      </motion.div>
    </section>
  );
};

export default Register;

